import {HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {
  BaseDaoService,
  handleError,
  SpacePermissions,
  User,
  UserTeam
} from '@q9elements/ui-kit/common';
import {Observable} from 'rxjs';

import {InviteUserApiPayload, InviteUsersBatchApiPayload} from '../models/spaces.interface';

export interface ElementsGptEnterpriseSettings {
  externalGenerationForEnterpriseEnabled: boolean;
  externalGenerationForSpacesDisabled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SpacesDaoService extends BaseDaoService {
  addSpace(values: {
    name: string;
    description: string;
    prefix: string;
    type: string;
  }): Observable<{newTeamIndex: number; newTeamId: string}> {
    return this.http
      .post<{
        newTeamIndex: number;
        newTeamId: string;
      }>(`${this.apiUrl}/organizations`, values)
      .pipe(handleError());
  }

  inviteUser(id: string, data: InviteUserApiPayload) {
    return this.http.post(`${this.apiUrl}/organizations/${id}/users`, data).pipe(handleError());
  }

  inviteUsersBatch(id: string, data: InviteUsersBatchApiPayload) {
    return this.http
      .post(`${this.apiUrl}/organizations/${id}/users/batch`, data)
      .pipe(handleError());
  }

  getTeamAdmins(id: string): Observable<User[]> {
    return this.http.get(`${this.apiUrl}/organizations/${id}/users/admins`) as Observable<User[]>;
  }

  updateSpace(id: string, data: unknown): Observable<UserTeam> {
    return this.http.put(`${this.apiUrl}/organizations/${id}`, data) as Observable<UserTeam>;
  }

  leaveSpace(id: string, targetOwner: string) {
    const params = new HttpParams().append('targetUser', targetOwner);

    return this.http
      .delete(`${this.apiUrl}/organizations/${id}/users`, {params: params})
      .pipe(handleError());
  }

  makeDefault(id: string) {
    return this.http.patch(`${this.apiUrl}/organizations/${id}/default`, null);
  }

  getSpaceEnterprisePermissions(): Observable<SpacePermissions> {
    return this.http.get(
      `${this.apiUrl}/organizations/enterprise/permissions`
    ) as Observable<SpacePermissions>;
  }

  getEnterpriseSettings(): Observable<ElementsGptEnterpriseSettings> {
    return this.http.get<ElementsGptEnterpriseSettings>(
      `${this.apiUrl}/elements-gpt/enterprise-settings`
    );
  }

  getSpaceUsers(id: string) {
    return this.http.get(`${this.apiUrl}/organizations/${id}/user`).pipe(handleError());
  }

  deleteSpace(id: string) {
    return this.http.delete(`${this.apiUrl}/organizations/${id}`).pipe(handleError());
  }

  loadEnterprise(params: {itemType: 'space' | 'user'}) {
    return this.http.get(`${this.apiUrl}/account/enterprise/admins`, {params});
  }

  joinSpace(id: string) {
    return this.http.post(`${this.apiUrl}/organizations/public/${id}/users`, null);
  }

  public getUserInfo() {
    return this.http.get<User>(`${this.apiUrl}/account`).pipe(handleError());
  }
}
